import React, { useRef } from "react";
import Link from "next/link";

function CardLabel({ left, right }) {
  return (
    <div className="absolute bottom-0 left-0 right-0 flex justify-between bg-black/75 px-[14px] py-[10px] backdrop-blur-[6px] border-t border-[#1e1e1e]">
      <span className="text-[10px] font-semibold tracking-[0.8px] text-[#f3f3f3] uppercase">{left}</span>
      <span className="text-[10px] font-medium text-[#d0d0d0]">{right}</span>
    </div>
  );
}

const getImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  
  const apiUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/$/, '') || "";
  
  if (url.includes("/uploads/")) {
    const cleanPath = url.substring(url.indexOf("/uploads/"));
    return `${apiUrl}${cleanPath}`;
  }
  
  if (url.startsWith("uploads/") || url.startsWith("/uploads/")) {
    const cleanPath = url.startsWith("/") ? url : `/${url}`;
    return `${apiUrl}${cleanPath}`;
  }
  
  if (url.startsWith("http")) return url;
  return url;
};

const formatStatValue = (val) => {
  if (!val) return "";
  const match = val.match(/^([\d.]+)([a-zA-Z%+]+)$/);
  if (match) {
    return (
      <>
        {match[1]}
        <span className="text-[16px] sm:text-[20px] text-[#F48428]">{match[2]}</span>
      </>
    );
  }
  return val;
};

export default function AboutBanner({ data }) {
  const bannerRef = useRef(null);
  const c0 = useRef(null);
  const c1 = useRef(null);
  const c2 = useRef(null);

  const cardBase = {
    width: 300,
    height: 180,
    borderRadius: 12,
    background: "#0e0e0e",
    border: "0.5px solid #1e1e1e",
    overflow: "hidden",
    position: "relative",
    cursor: "pointer",
  };

  const displayStats = data?.stats && data.stats.length > 0 ? data.stats : [
    { value: "8yr", label: "In business" },
    { value: "2.4K", label: "Active users" },
    { value: "40+", label: "Countries" },
    { value: "99.9%", label: "Uptime SLA" },
  ];

  const buttons = data?.buttons && data.buttons.length > 0 ? data.buttons : [
    { text: "Our story", link: "#" },
    { text: "Meet the team", link: "/contact" }
  ];

  const displayImages = data?.images && data.images.length > 0 ? data.images : [
    { url: "/assets/img/about_dashboard_3.png", alt: "ERP Overview", tags: ["ERP", "Overview"] },
    { url: "/assets/img/about_dashboard_2.png", alt: "CRM Analytics", tags: ["CRM", "Analytics"] },
    { url: "/assets/img/about_dashboard_3.png", alt: "Odoo Enterprise", tags: ["Odoo", "Enterprise"] }
  ];

  return (
    <div ref={bannerRef} className="relative overflow-hidden rounded-2xl bg-transparent px-4 sm:px-12 pb-8 pt-4">

      {/* ── Main content grid ── */}
      <div className="relative grid grid-cols-1 lg:grid-cols-[1fr_auto] items-end gap-[60px]">

        {/* ── Left column ── */}
        <div className="mb-10 lg:mb-20 flex flex-col items-start text-left lg:items-start lg:text-left">
          {/* Headline */}
          <h1 className="ab-headline mt-2 mb-[30px] max-w-full xl:max-w-[900px] text-[20px] sm:text-[34px] md:text-[42px] font-medium leading-[1.2] text-[#f0f0f0] pr-4">
            {data?.title || "We build infrastructure that"}{" "}
            {data?.highlightedText && (
              <em className="not-italic text-[#F48428]">{data.highlightedText}</em>
            )}{" "}
            {data?.title2 || "the next generation runs on"}
          </h1>

          {/* Subtext */}
          <p className="ab-subtext mb-[30px] max-w-full xl:max-w-[850px] text-[13px] sm:text-[17px] leading-[1.6] text-[#9e9e9e] pr-4">
            {data?.description || "From a two-person apartment in Mumbai to a globally distributed team — we have spent twelve years solving hard problems with quiet precision and relentless craft."}
          </p>

          {/* Stats row */}
          <div className="ab-stats mb-[30px] flex flex-wrap justify-start gap-6 sm:gap-[48px] border-b border-[#141414] pb-[25px] w-full">
            {displayStats.map((s, index) => (
              <div key={index}>
                <div className="text-[24px] sm:text-[40px] font-medium text-[#e0e0e0]">
                  {formatStatValue(s.value)}
                </div>
                <div className="mt-[5px] text-[9.5px] sm:text-[13px] tracking-[0.5px] text-[#a0a0a0] uppercase">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="ab-actions flex flex-wrap sm:flex-nowrap justify-start gap-3">
            {buttons.map((btn, index) => {
              if (index === 0) {
                return (
                  <Link
                    key={index}
                    href={btn.link || "#"}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#F48428] px-5 py-[10px] text-[13px] font-bold text-[#0a0a0a] transition-colors hover:bg-[#e0721e] whitespace-nowrap"
                  >
                    {btn.text}
                  </Link>
                );
              }
              return (
                <Link
                  key={index}
                  href={btn.link || "/contact"}
                  className="ab-btn-ghost inline-flex items-center gap-[7px] rounded-lg border border-[#181818] bg-transparent px-5 py-[10px] text-[13px] font-medium text-[#a0a0a0] transition-all hover:border-[#333] whitespace-nowrap"
                >
                  {btn.text}
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Right column: floating cards ── */}
        <div className="hidden lg:flex flex-col gap-6 pb-1 shrink-0">
          {displayImages.slice(0, 3).map((img, i) => {
            const marginClasses = [24, 0, 16];
            const ml = marginClasses[i] !== undefined ? marginClasses[i] : 12;
            const refObj = [c0, c1, c2][i];

            return (
              <div
                key={i}
                ref={refObj}
                className={`ab-card-${i} stagger-in-${i + 1}`}
                style={{ ...cardBase, width: 360, height: 180, marginLeft: ml }}
              >
                <img
                  src={getImageUrl(img.url)}
                  alt={img.alt || "About card"}
                  className="w-full h-full object-cover opacity-80 hover:opacity-100 transition-opacity"
                />
                <CardLabel
                  left={img.tags && img.tags[0] ? img.tags[0] : (i === 0 ? "ERP" : i === 1 ? "CRM" : "Odoo")}
                  right={img.tags && img.tags[1] ? img.tags[1] : (i === 0 ? "Overview" : i === 1 ? "Analytics" : "Enterprise")}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
